import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtMilli, MONTHS_AR } from "../../lib/mshopCalc";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const TYPES = [["sale", "البيع"], ["purchase", "الشراء"], ["expense", "المصاريف"]];
const labelType = (t) => TYPES.find(([k]) => k === t)?.[1] || t;
const amountOf = (op) => op.inputs?.cash != null || op.inputs?.bank != null
    ? { cash: op.inputs?.cash || "0", bank: op.inputs?.bank || "0" }
    : { cash: op.cash_delta || "0", bank: "0" };

function groupBy(items, keyFn) {
    const m = new Map();
    items.forEach((it) => { const k = keyFn(it); m.set(k, [...(m.get(k) || []), it]); });
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
}
function LevelRow({ label, count, onClick }) {
    return <button onClick={onClick} className="flex w-full items-center justify-between rounded-2xl bg-white px-5 py-4 text-right shadow-sm ring-1 ring-[#E7E5DF] hover:bg-[#FBFAF7]">
        <span className="font-heading text-lg font-bold text-neutral-900">{label}</span>
        <span className="flex items-center gap-2 text-sm text-neutral-500">{count} عملية <ChevronLeft className="h-4 w-4" /></span>
    </button>;
}
function OpDetail({ op, canDelete, onDeleted, onClose }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const modern = op.inputs?.cash !== undefined || op.inputs?.bank !== undefined;
    const rows = modern ? [
        ...(op.type !== "expense" ? [["الوزن (جرام)", op.inputs?.weight]] : []),
        ["الكاش (ر.ع)", op.inputs?.cash], ["البنك (ر.ع)", op.inputs?.bank],
    ] : [
        ["الوزن (جرام)", op.inputs?.weight],
        ["الكاش (ر.ع)", op.cash_delta], ["البنك (ر.ع)", "0"],
    ];
    const del = async () => {
        setBusy(true);
        try {
            await api.post(`/mshop/ops/${op.id}/admin-delete`, { reason });
            toast.success("تم إلغاء العملية من الأرصدة مع حفظ سجل الحذف");
            onDeleted();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent dir="rtl" className="max-h-[90vh] overflow-y-auto" data-testid="mshop-op-detail">
            <DialogHeader><DialogTitle className="text-right">{labelType(op.type)} — {op.oman_date} {op.oman_time}</DialogTitle></DialogHeader>
            <div className="text-sm text-neutral-500">الموظف: {op.employee_name}</div>
            <div className="rounded-xl bg-[#F3F2EE] px-4 py-3"><div className="text-xs text-neutral-500">البيان</div><div className="mt-1 whitespace-pre-wrap font-semibold">{op.note || "لا يوجد بيان"}</div></div>
            {op.deleted_at && <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-800">محذوفة بواسطة {op.deleted_by_name} • السبب: {op.delete_reason}</div>}
            <div className="divide-y divide-dashed divide-[#E7E5DF]">
                {rows.map(([label, value]) => <div key={label} className="flex items-center justify-between py-3"><span className="text-neutral-700">{label}</span><span dir="ltr" className="font-numbers font-bold">{fmtMilli(value)}</span></div>)}
            </div>
            {!modern && <div className="text-xs text-amber-700">عملية من النظام السابق؛ المبلغ النقدي محفوظ كما سُجّل سابقاً.</div>}
            {canDelete && !op.deleted_at && <div className="mt-2 space-y-2 rounded-xl bg-[#FBFAF7] p-3 ring-1 ring-[#E7E5DF]">
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="سبب الحذف (إلزامي)" className="h-20 w-full rounded-lg border border-[#DAD7CF] bg-white p-2 text-sm" />
                <button onClick={del} disabled={busy || reason.trim().length < 3} className="inline-flex h-10 items-center gap-2 rounded-lg bg-rose-700 px-4 text-sm font-bold text-white disabled:opacity-50">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} حذف إداري
                </button>
            </div>}
        </DialogContent>
    </Dialog>;
}

export default function RecordsBrowser({ employeeId, initialType = "sale", hideTabs = false }) {
    const [type, setType] = useState(initialType);
    const [items, setItems] = useState(null);
    const [canDelete, setCanDelete] = useState(false);
    const [path, setPath] = useState([]);
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        setItems(null);
        try {
            const r = await api.get("/mshop/ops", { params: { type, employee_id: employeeId, include_deleted: !!employeeId } });
            setItems(r.data.items); setCanDelete(r.data.can_delete);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setItems([]); }
    }, [type, employeeId]);
    useEffect(() => { load(); }, [load]);
    const [y, m, d] = path;
    const level = useMemo(() => {
        if (!items) return [];
        let list = items;
        if (y) list = list.filter((o) => o.oman_date.startsWith(y));
        if (m) list = list.filter((o) => o.oman_date.slice(5, 7) === m);
        if (d) return list.filter((o) => o.oman_date.slice(8, 10) === d);
        const slice = !y ? [0, 4] : !m ? [5, 7] : [8, 10];
        return groupBy(list, (o) => o.oman_date.slice(...slice));
    }, [items, y, m, d]);
    const crumb = [y, m && MONTHS_AR[parseInt(m, 10) - 1], d && `يوم ${parseInt(d, 10)}`].filter(Boolean).join(" ← ");
    return <div dir="rtl" data-testid="mshop-records">
        {!hideTabs && <div className="mb-4 flex gap-2 rounded-2xl bg-white p-1.5 ring-1 ring-[#E7E5DF]">
            {TYPES.map(([k, l]) => <button key={k} onClick={() => { setType(k); setPath([]); }} className={`flex-1 rounded-xl py-2.5 text-sm font-bold ${type === k ? "bg-[#1F1D1A] text-[#F3DFA2]" : "text-neutral-600 hover:bg-neutral-50"}`}>{l}</button>)}
        </div>}
        {path.length > 0 && <div className="mb-3 flex items-center gap-3">
            <button onClick={() => setPath(path.slice(0, -1))} className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-sm font-semibold ring-1 ring-[#E7E5DF]"><ArrowRight className="h-4 w-4" /> رجوع</button>
            <span className="text-sm text-neutral-500">{crumb}</span>
        </div>}
        {items === null ? <div className="py-10 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-neutral-400" /></div> : level.length === 0 ?
            <div className="rounded-2xl bg-white py-10 text-center text-sm text-neutral-500 ring-1 ring-[#E7E5DF]">لا توجد عمليات</div> : d ?
            <div className="space-y-2">{level.map((op) => {
                const amounts = amountOf(op);
                return <button key={op.id} onClick={() => setOpen(op)} className={`flex w-full items-center gap-3 rounded-2xl bg-white px-4 py-3 text-right shadow-sm ring-1 ring-[#E7E5DF] ${op.deleted_at ? "opacity-60" : ""}`}>
                    <span className="font-numbers text-sm text-neutral-500">{op.oman_time?.slice(0, 5)}</span>
                    <span className="flex-1">
                        <span className="block font-bold text-neutral-900">{op.inputs?.weight != null ? `${fmtMilli(op.inputs.weight)} جم • ` : ""}كاش {fmtMilli(amounts.cash)} • بنك {fmtMilli(amounts.bank)} ر.ع</span>
                        <span className="block text-xs text-neutral-500">{op.note || "بدون بيان"}</span>
                    </span>
                    {op.deleted_at && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-bold text-rose-700">محذوفة</span>}
                    <ChevronLeft className="h-4 w-4 text-neutral-400" />
                </button>;
            })}</div> :
            <div className="space-y-2">{level.map(([k, list]) => <LevelRow key={k} label={!y ? k : !m ? MONTHS_AR[parseInt(k, 10) - 1] : `يوم ${parseInt(k, 10)} (${y}-${m}-${k})`} count={list.length} onClick={() => setPath([...path, k])} />)}</div>}
        {open && <OpDetail op={open} canDelete={canDelete} onClose={() => setOpen(null)} onDeleted={() => { setOpen(null); load(); }} />}
    </div>;
}
